/**
 * Delay buffer (PMA-BCAST-004).
 *
 * Every event source (socket, first load, reconnect, 30 s reconcile) funnels
 * through `ingest`. An event whose server receive time is later than
 * `serverNow - delay` is scheduled for receive time + delay (expressed in
 * client time via the measured clock offset) and is NEVER shown immediately;
 * an older event is released at once. An undo removes a still-pending event
 * (so its call-out never fires) or reports a withdrawal for a released one.
 * Layout messages never enter this buffer.
 */
import type { MatchEvent } from "../../store/useMatchLogStore";
import { parseServerMs } from "./serverTime";

export type IngestSource = "live" | "snapshot";

export interface ReleaseInfo {
  /** True when a call-out may fire (live arrival or buffered release). */
  allowCallout: boolean;
  /**
   * Ms since the delayed release instant (received_at + delay). 0 for a
   * buffered release. Lets the caller call out a RECENT snapshot-only event.
   */
  ageMs: number;
}

export interface DelayBufferOptions {
  getDelayMs: () => number;
  onRelease: (event: MatchEvent, info: ReleaseInfo) => void;
  /** A released event was undone (remove it and withdraw its call-out). */
  onWithdraw: (eventId: string) => void;
  now?: () => number;
}

export const eventKey = (e: Pick<MatchEvent, "_id" | "client_id">): string =>
  String(e._id ?? e.client_id ?? "");

interface Entry {
  event: MatchEvent;
  receivedClientMs: number;
  timer: ReturnType<typeof setTimeout> | null;
  released: boolean;
}

export class DelayBuffer {
  private entries = new Map<string, Entry>();
  /** server clock - client clock, measured on each state fetch. */
  private offsetMs = 0;
  private readonly now: () => number;

  constructor(private readonly opts: DelayBufferOptions) {
    this.now = opts.now ?? (() => Date.now());
  }

  setServerTime(serverTimeIso: string | undefined) {
    const ms = parseServerMs(serverTimeIso);
    if (Number.isFinite(ms)) this.offsetMs = ms - this.now();
  }

  /** Client-clock instant corresponding to "now minus delay" playback time. */
  getOffsetMs() {
    return this.offsetMs;
  }

  has(id: string) {
    return this.entries.has(id);
  }

  isPending(id: string) {
    const e = this.entries.get(id);
    return Boolean(e && !e.released);
  }

  pendingCount() {
    let n = 0;
    this.entries.forEach((e) => {
      if (!e.released) n += 1;
    });
    return n;
  }

  /** Returns "released" | "scheduled" | "duplicate". */
  ingest(
    event: MatchEvent,
    source: IngestSource,
    receivedAtIso?: string | null,
  ): "released" | "scheduled" | "duplicate" {
    const id = eventKey(event);
    if (!id || this.entries.has(id)) return "duplicate";
    // A client_id duplicate of an already-known event (the socket delivers
    // `_id`, the snapshot may carry both): also dedupe on the other key.
    if (event.client_id && this.entries.has(String(event.client_id)))
      return "duplicate";

    const delay = this.opts.getDelayMs();
    const serverMs = parseServerMs(receivedAtIso);
    // Live socket events without a receive time are "just received".
    const receivedClientMs = Number.isFinite(serverMs)
      ? serverMs - this.offsetMs
      : this.now();
    const releaseAt = receivedClientMs + delay;
    const wait = releaseAt - this.now();

    const entry: Entry = {
      event,
      receivedClientMs,
      timer: null,
      released: false,
    };
    this.entries.set(id, entry);
    if (event.client_id && String(event.client_id) !== id) {
      this.entries.set(String(event.client_id), entry);
    }

    if (wait <= 0) {
      entry.released = true;
      this.opts.onRelease(event, {
        allowCallout: source === "live",
        ageMs: Math.max(0, -wait),
      });
      return "released";
    }
    entry.timer = setTimeout(() => {
      entry.timer = null;
      entry.released = true;
      this.opts.onRelease(event, { allowCallout: true, ageMs: 0 });
    }, wait);
    return "scheduled";
  }

  /** Undo: cancel a pending event, or withdraw a released one. */
  undo(id: string): "cancelled" | "withdrawn" | "unknown" {
    const entry = this.entries.get(id);
    if (!entry) return "unknown";
    const key = eventKey(entry.event);
    this.entries.delete(id);
    this.entries.delete(key);
    if (entry.event.client_id)
      this.entries.delete(String(entry.event.client_id));
    if (!entry.released) {
      if (entry.timer) clearTimeout(entry.timer);
      entry.timer = null;
      return "cancelled";
    }
    this.opts.onWithdraw(key);
    return "withdrawn";
  }

  /**
   * Heal a missed undo: any known event that is absent from a fresh snapshot
   * and was received before the snapshot's own server time (minus a small
   * grace window) was undone.
   */
  reconcileAbsent(presentIds: Set<string>, serverTimeIso: string) {
    const serverMs = parseServerMs(serverTimeIso);
    if (!Number.isFinite(serverMs)) return;
    const cutoffClient = serverMs - this.offsetMs - 2000;
    const seen = new Set<Entry>();
    Array.from(this.entries.values()).forEach((entry) => {
      if (seen.has(entry)) return;
      seen.add(entry);
      const key = eventKey(entry.event);
      const present =
        presentIds.has(key) ||
        (entry.event.client_id !== undefined &&
          presentIds.has(String(entry.event.client_id)));
      if (!present && entry.receivedClientMs < cutoffClient) {
        this.undo(key);
      }
    });
  }

  dispose() {
    const seen = new Set<Entry>();
    this.entries.forEach((e) => {
      if (seen.has(e)) return;
      seen.add(e);
      if (e.timer) clearTimeout(e.timer);
    });
    this.entries.clear();
  }
}
